import { useEffect, useState } from "react";
import { SupportLookOnlyNote } from "@/components/SupportLookOnlyNote";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mail, Plus, Trash2, Star, Send, Loader2, Server, Pencil } from "lucide-react";

const TEAL = "#2f6f6b";
const EMPTY = { label: "", host: "", port: 587, username: "", password: "", from_name: "", from_email: "", use_tls: true, is_default: false,
  imap_enabled: false, imap_host: "", imap_port: 993, imap_username: "", imap_password: "" };

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] font-medium text-slate-600">{label}</Label>
      {children}
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
    </div>
  );
}

function SenderForm({ initial, editingId, onDone, onCancel }) {
  const [f, setF] = useState(() => {
    const merged = { ...EMPTY, ...initial, password: "", imap_password: "" };
    // The server returns null for unset IMAP fields; keep the inputs controlled.
    ["username", "from_name", "imap_host", "imap_username"].forEach((k) => { merged[k] = merged[k] || ""; });
    // ...and false/missing flags come back as null, which the API rejects as a boolean.
    merged.is_default = !!merged.is_default;
    merged.use_tls = merged.use_tls !== false;
    merged.imap_port = merged.imap_port || 993;
    merged.imap_enabled = !!merged.imap_enabled;
    return merged;
  });
  const [saving, setSaving] = useState(false);
  const [testingImap, setTestingImap] = useState(false);
  const testImap = async () => {
    setTestingImap(true);
    try {
      const r = await api.smtpImapTest(editingId);
      toast.success(`Connected. ${r.messages} message${r.messages === 1 ? "" : "s"} in the inbox.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not connect."); }
    finally { setTestingImap(false); }
  };
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.label || !f.host || !f.from_email) { toast.error("Label, host and from-address are required."); return; }
    setSaving(true);
    try {
      if (editingId) await api.smtpUpdateSender(editingId, f);
      else await api.smtpCreateSender(f);
      toast.success(editingId ? "Sender updated." : "Sender added.");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save."); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="sender-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Label"><Input value={f.label} onChange={(e) => set("label", e.target.value)} placeholder="No-reply" data-testid="sender-label" /></Field>
        <Field label="From address"><Input value={f.from_email} onChange={(e) => set("from_email", e.target.value)} placeholder="no-reply@prydessupport.com" data-testid="sender-from-email" /></Field>
        <Field label="From name (optional)"><Input value={f.from_name} onChange={(e) => set("from_name", e.target.value)} placeholder="Prydes Support" data-testid="sender-from-name" /></Field>
        <Field label="SMTP host"><Input value={f.host} onChange={(e) => set("host", e.target.value)} placeholder="smtp.yourserver.com" data-testid="sender-host" /></Field>
        <Field label="Port" hint="587 (STARTTLS) or 465 (SSL)"><Input type="number" value={f.port} onChange={(e) => set("port", e.target.value)} data-testid="sender-port" /></Field>
        <Field label="Username (optional)"><Input value={f.username} onChange={(e) => set("username", e.target.value)} placeholder="smtp login" data-testid="sender-username" /></Field>
        <Field label={editingId ? "Password (leave blank to keep current; type it again if you change the server, port or username)" : "Password"}><Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="••••••••" data-testid="sender-password" /></Field>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-[12px] text-slate-600"><Switch checked={f.use_tls} onCheckedChange={(v) => set("use_tls", v)} data-testid="sender-tls" /> Use STARTTLS</label>
        <label className="flex items-center gap-2 text-[12px] text-slate-600"><Switch checked={f.is_default} onCheckedChange={(v) => set("is_default", v)} data-testid="sender-default" /> Make default</label>
      </div>
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="sender-imap">
        <label className="flex items-center gap-2 text-[12px] font-medium text-slate-700">
          <Switch checked={f.imap_enabled} onCheckedChange={(v) => set("imap_enabled", v)} data-testid="sender-imap-enabled" />
          Track replies &amp; bounces for bulk email (reads this mailbox over IMAP)
        </label>
        {f.imap_enabled && (
          <>
            <p className="text-[11px] text-slate-500">
              The mailbox is opened read-only; nothing is marked read or changed, and message contents are not stored.
              For Google Workspace use <span className="font-mono">imap.gmail.com</span>, port 993, with IMAP enabled for the account.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="IMAP host"><Input value={f.imap_host} onChange={(e) => set("imap_host", e.target.value)} placeholder="imap.gmail.com" data-testid="sender-imap-host" /></Field>
              <Field label="IMAP port" hint="993 (SSL)"><Input type="number" value={f.imap_port} onChange={(e) => set("imap_port", e.target.value)} data-testid="sender-imap-port" /></Field>
              <Field label="IMAP username" hint="Blank = SMTP username or from address"><Input value={f.imap_username} onChange={(e) => set("imap_username", e.target.value)} data-testid="sender-imap-username" /></Field>
              <Field label="IMAP password" hint="Blank = reuse the SMTP password (leave blank to keep current; type it again if you change the IMAP server or username)"><Input type="password" value={f.imap_password} onChange={(e) => set("imap_password", e.target.value)} placeholder="••••••••" data-testid="sender-imap-password" /></Field>
            </div>
            {editingId && (
              <Button size="sm" variant="outline" onClick={testImap} disabled={testingImap} data-testid="sender-imap-test">
                {testingImap ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}Test IMAP connection (save first)
              </Button>
            )}
          </>
        )}
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Button size="sm" style={{ backgroundColor: TEAL }} onClick={save} disabled={saving} data-testid="sender-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}{editingId ? "Save changes" : "Add sender"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} data-testid="sender-cancel">Cancel</Button>
      </div>
    </div>
  );
}

export default function SmtpSettings() {
  const [senders, setSenders] = useState([]);
  const [editing, setEditing] = useState(null);   // sender id or "new" or null
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState("");

  const load = () => api.smtpSenders().then((d) => setSenders(d.senders || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this SMTP sender?")) return;
    try { await api.smtpDeleteSender(id); toast.success("Sender deleted."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const makeDefault = async (id) => {
    try { await api.smtpSetDefault(id); toast.success("Default sender updated."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const sendTest = async (id) => {
    if (!testTo) { toast.error("Enter a test recipient email."); return; }
    setTesting(id);
    try { await api.smtpTest(testTo, id); toast.success(`Test email sent to ${testTo}.`); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed."); }
    finally { setTesting(""); }
  };
  const onDone = () => { setEditing(null); load(); };

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="smtp-settings">
      <div className="flex items-center gap-2.5 mb-1"><Mail className="h-5 w-5" style={{ color: TEAL }} /><h1 className="text-xl font-semibold font-brand text-slate-800">Email Senders (SMTP)</h1></div>
      <SupportLookOnlyNote />
      <p className="text-sm text-slate-500 mb-5">Send all app email through your own SMTP server — nothing routes through a third-party mail service. Add one or more senders and pick a default (e.g. your no-reply address). You'll be able to assign different senders to different parts of the app later.</p>

      <div className="flex items-center gap-2 mb-4">
        <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com — for test sends" className="max-w-xs" data-testid="test-recipient" />
        {editing !== "new" && <Button size="sm" style={{ backgroundColor: TEAL }} onClick={() => setEditing("new")} data-testid="add-sender-btn"><Plus className="h-4 w-4 mr-1.5" />Add sender</Button>}
      </div>

      {editing === "new" && <div className="mb-4"><SenderForm initial={EMPTY} editingId={null} onDone={onDone} onCancel={() => setEditing(null)} /></div>}

      <div className="space-y-3">
        {senders.length === 0 && editing !== "new" && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400" data-testid="no-senders">
            <Server className="h-6 w-6 mx-auto mb-2 text-slate-300" />No SMTP senders yet. Add your no-reply address to start sending email from the app.
          </div>
        )}
        {senders.map((s) => editing === s.id ? (
          <SenderForm key={s.id} initial={s} editingId={s.id} onDone={onDone} onCancel={() => setEditing(null)} />
        ) : (
          <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`sender-${s.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-slate-800">{s.label || s.from_email}</h3>
                  {s.is_default && <Badge className="border bg-emerald-50 text-emerald-700 border-emerald-200"><Star className="h-3 w-3 mr-1" />Default</Badge>}
                  {!s.has_password && <Badge className="border bg-amber-50 text-amber-700 border-amber-200">No password set</Badge>}
                </div>
                <p className="text-[12px] text-slate-500 mt-0.5">{s.from_name ? `${s.from_name} · ` : ""}{s.from_email}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{s.host}:{s.port} · {s.use_tls ? "STARTTLS" : (s.port === 465 ? "SSL" : "plain")}{s.username ? ` · ${s.username}` : ""}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="outline" onClick={() => sendTest(s.id)} disabled={testing === s.id} data-testid={`test-${s.id}`}>{testing === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
                {!s.is_default && <Button size="sm" variant="outline" onClick={() => makeDefault(s.id)} data-testid={`default-${s.id}`}><Star className="h-4 w-4" /></Button>}
                <Button size="sm" variant="outline" onClick={() => setEditing(s.id)} data-testid={`edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" className="text-rose-600" onClick={() => remove(s.id)} data-testid={`delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
